import { todayKolkata, validPaymentDay } from '../utils/paymentDates';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import InvoiceProductSelector from './InvoiceProductSelector';
import DeliveryPanel from './DeliveryPanel';
import { downloadInvoiceReceipt } from '../utils/invoiceReceipt';
import { downloadLatestInvoiceReceipt, getSavedInvoice } from '../services/invoiceReceiptApi';
import { toPaise, fromPaise, sumPaise, invoicePaise } from '../utils/inr';

const todayLocal = todayKolkata;

const dateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const money = value => Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-IN') : '—';

const blankItem = () => ({
  productId: null,
  label: '',
  amount: 0,
  qty: 1,
});

const normalizeLoadedItems = (items = []) =>
  items.map((item) => ({
    _id: item._id,
    productId: item.productId || null,
    label: item.label || '',
    amount: item.amount,
    qty: Math.max(1, Number(item.qty) || 1),
  }));

export default function RunningPaymentForm() {
  // ============================================================
  // FORM STATE
  // ============================================================

  const [customerName, setCustomerName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [notes, setNotes] = useState('');
  const [organization, setOrganization] = useState('The Bengal Craft');
  const [paymentMode, setPaymentMode] = useState('UPI');
  const [discount, setDiscount] = useState(0);
  const [amountReceived, setAmountReceived] = useState(0);

  const [feeLines, setFeeLines] = useState([blankItem()]);

// Product Master
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productRevision, setProductRevision] = useState(0);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [deliveryId, setDeliveryId] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const onDeliveryChanged = useCallback(() => {}, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [whatsappError, setWhatsappError] = useState('');
  const [receivedError, setReceivedError] = useState('');

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayLocal());

  const [existingMode, setExistingMode] = useState(false);
  const [loadedDoc, setLoadedDoc] = useState(null);

  const [extraPayment, setExtraPayment] = useState(0);
  const [extraPaymentMode, setExtraPaymentMode] = useState('UPI');
  const [extraPaymentDate, setExtraPaymentDate] = useState('');

  const sanitizeWhatsApp = (value) =>
    (value || '').replace(/[^+\d]/g, '');

  const isValidWhatsApp = (value) =>
    /^\+?\d{7,15}$/.test(value || '');

  const inr = money;

  // ============================================================
  // RESET FORM
  // ============================================================

  const resetForm = () => {
    setSavedInvoiceId(null); setDeliveryId(null);
    setCustomerName('');
    setWhatsapp('');
    setNotes('');
    setOrganization('The Bengal Craft');
    setPaymentMode('UPI');
    setDiscount(0);
    setAmountReceived(0);
    setFeeLines([blankItem()]);
    setInvoiceNumber('');
    setInvoiceDate(todayLocal());
    setExistingMode(false);
    setLoadedDoc(null);
    setExtraPayment(0);
    setExtraPaymentMode('UPI');
    setExtraPaymentDate(todayKolkata());
    setWhatsappError('');
    setReceivedError('');

    // Prevent a previously selected invoice from reloading on refresh.
    const url = new URL(window.location.href);
    if (url.searchParams.has('invoice')) {
      url.searchParams.delete('invoice');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  };

  // ============================================================
  // LOAD EXISTING INVOICE
  // ============================================================

  const populateInvoice = (data) => {
    setSavedInvoiceId(null);
    setLoadedDoc(data);
    setExistingMode(true);

    setInvoiceNumber(data.invoiceNumber || '');
    setInvoiceDate(dateInputValue(data.invoiceDate || data.date));

    setCustomerName(data.customerName || '');
    setWhatsapp(data.whatsapp || '');
    setNotes(data.notes || '');

    setOrganization(data.organization || 'The Bengal Craft');
    setPaymentMode(data.paymentMode || 'UPI');
    setDiscount(Number(data.discount || 0));

    // Preserve permanent IDs and product references.
    setFeeLines(normalizeLoadedItems(data.items || []));

    setAmountReceived(0);
    setExtraPayment(0);
    setExtraPaymentMode('UPI');
    setExtraPaymentDate(todayKolkata());
  };

  const loadInvoice = async (number) => {
    const reference = String(number || '').trim();

    if (!reference) {
      alert('Enter an invoice number');
      return;
    }

    try {
      const response = await axios.get(
        `/api/running/by-invoice/${encodeURIComponent(reference)}`
      );

      populateInvoice(response.data);
    } catch (error) {
      console.error('Invoice loading failed:', error);
      alert('Invoice not found or could not be loaded.');
    }
  };

  // Allows the report to open this form with ?invoice=INV000123.
  useEffect(() => {
    const requestedInvoice = new URLSearchParams(
      window.location.search
    ).get('invoice');

    if (!requestedInvoice) return;

    let cancelled = false;

    axios
      .get(
        `/api/running/by-invoice/${encodeURIComponent(requestedInvoice)}`
      )
      .then(({ data }) => {
        if (!cancelled) {
          populateInvoice(data);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Unable to preload invoice:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);
// ============================================================
// LOAD PRODUCT MASTER
// ============================================================

useEffect(() => {
  let cancelled = false;

  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsError('');
    setProducts([]);

    try {
      const response = await axios.get('/api/products', {
        params: {
          organization,
          active: 'true',
        },
      });

      if (!Array.isArray(response.data)) {
        throw new Error('Invalid Product Master response.');
      }

      if (!cancelled) {
        setProducts(response.data);
      }
    } catch (error) {
      if (!cancelled) {
        setProductsError(
          error?.response?.data?.error ||
          error.message ||
          'Unable to load products.'
        );
      }
    } finally {
      if (!cancelled) {
        setProductsLoading(false);
      }
    }
  };

  loadProducts();

  return () => {
    cancelled = true;
  };
}, [organization, productRevision]);
  const downloadReceipt = async (invoice) => {
    if (receiptBusy) return;
    setReceiptBusy(true);
    try { await downloadLatestInvoiceReceipt(invoice); }
    catch (error) { alert(error.response?.data?.error || error.message || 'Unable to generate receipt.'); }
    finally { setReceiptBusy(false); }
  };
  const closeDelivery = async () => {
    setDeliveryId(null);
    try { const latest = await getSavedInvoice(loadedDoc); populateInvoice(latest); setSavedInvoiceId(latest._id); }
    catch { setSavedInvoiceId(null); alert('Unable to refresh saved invoice after delivery. Reload the invoice before editing again.'); }
  };

  // ============================================================
  // ITEM MANAGEMENT
  // ============================================================

  const handleAddLine = () => {
    setSavedInvoiceId(null);
    setFeeLines((previous) => [...previous, blankItem()]);
  };

  const handleRemoveLine = index => {
    setSavedInvoiceId(null);
    setFeeLines(previous => previous.filter((_, position) => position !== index));
  };

  const handleLineChange = (index, key, value) => {
    setFeeLines((previous) =>
      previous.map((line, i) =>
        i === index ? { ...line, [key]: value } : line
      )
    );
  };

    const handleProductChange = (index, product) => {
    setSavedInvoiceId(null);
    setFeeLines(previous => previous.map((line, position) => position === index && !line._id ? {
      ...line, productId: product?._id || null, label: product?.name || '', amount: product ? Number(product.price) : 0
    } : line));
  };
  const newProductsValid = () => feeLines.filter(item => !item._id).every(item => products.some(product =>
    product._id === item.productId && product.organization === organization && product.isActive === true));

  // ============================================================
  // CALCULATIONS
  // ============================================================

  const finance = (() => {
    try {
      const calculation = invoicePaise(feeLines.map(line => ({ amount: line.amount ?? 0, qty: Number(line.qty) || 1 })), discount ?? 0);
      const receivedPaise = toPaise(amountReceived ?? 0, 'Received amount');
      const displayedPaise = existingMode && loadedDoc
        ? sumPaise([toPaise(loadedDoc.amountReceived, 'Historical received balance'), toPaise(extraPayment ?? 0, 'New payment')]) : receivedPaise;
      const duePaise = Math.max(calculation.total - displayedPaise, 0);
      return { gross: fromPaise(calculation.gross), totalAmount: fromPaise(calculation.total), received: fromPaise(receivedPaise),
        balanceDue: fromPaise(Math.max(calculation.total - receivedPaise, 0)), dispReceived: fromPaise(displayedPaise), dispDue: fromPaise(duePaise),
        settleStatus: receivedPaise <= 0 ? 'due' : receivedPaise < calculation.total ? 'partial' : 'paid',
        dispStatus: existingMode && loadedDoc ? (duePaise === 0 ? 'paid' : displayedPaise > 0 ? 'partial' : 'due')
          : receivedPaise <= 0 ? 'due' : receivedPaise < calculation.total ? 'partial' : 'paid', error: '' };
    } catch (error) {
      return { gross: NaN, totalAmount: NaN, received: Number(amountReceived), balanceDue: NaN,
        dispReceived: loadedDoc?.amountReceived ?? amountReceived, dispDue: NaN, settleStatus: 'due', dispStatus: 'reconcile', error: error.message };
    }
  })();
  const { gross, totalAmount, received, balanceDue, settleStatus, dispReceived, dispDue, dispStatus, error: financialError } = finance;

  // ============================================================
  // CREATE NEW INVOICE
  // ============================================================

  const handleSubmit = async () => {
    if (financialError) return alert(financialError);
    if (!customerName.trim()) {
      return alert('Customer name is required');
    }
    // Every new invoice item must reference an active product
// belonging to the selected organization.

if (productsLoading || productsError) {
  return alert(
    'Product Master is unavailable. Please wait and try again.'
  );
}

const invalidProduct = feeLines.some((item) => {
  if (!item.productId) return true;

  return !products.some(
    (product) =>
      product._id === item.productId &&
      product.organization === organization &&
      product.isActive === true
  );
});

if (invalidProduct) {
  return alert(
    'Every invoice item must be selected from the active Product Master.'
  );
}

    if (feeLines.length === 0 || gross <= 0) {
      return alert('Add at least one item with amount > 0');
    }

    if (totalAmount <= 0) {
      return alert('Total must be > 0 (check discount).');
    }

    if (whatsapp && !isValidWhatsApp(whatsapp)) {
      return alert('Please enter a valid WhatsApp number');
    }

    if (received > 0 && !validPaymentDay(invoiceDate)) return alert('A valid invoice date is required for the initial payment');

    if (received > totalAmount) {
      return alert('Received amount cannot be greater than Total');
    }

    try {
      setIsSubmitting(true);

      const response = await axios.post('/api/running', {
        customerName,
        whatsapp,
        items: feeLines.map((item) => {

  const product = products.find(

    (entry) => entry._id === item.productId

  );

  return {

    productId: product._id,

    label: product.name,

    amount: Number(item.amount),

    qty: Math.max(1, Number(item.qty) || 1),

  };

}),
        totalAmount,
        notes,
        paymentMode,
        discount,
        organization,
        amountReceived: received,
        balanceDue,
        status: settleStatus,
        invoiceNumber: invoiceNumber || undefined,
        invoiceDate,
        initialPaymentDate: invoiceDate,
      });

      const saved = response.data;

      let receiptCreated = false;
      try { downloadInvoiceReceipt(saved); receiptCreated = true; } catch { /* The invoice is saved; never retry the financial write for a receipt error. */ }
      alert(receiptCreated ? 'Invoice created successfully. Receipt downloaded.' : 'Invoice created successfully, but receipt generation failed. Download it from the report.');

      resetForm();
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error.message;

      alert(`Error creating invoice: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // UPDATE EXISTING INVOICE
  // ============================================================

  const handleUpdate = async () => {
    if (isSubmitting) return;
    setSavedInvoiceId(null);
    if (feeLines.some(item => !item._id) && (productsLoading || productsError || !newProductsValid())) return alert('Every new invoice item must be selected from the active Product Master.');
    if (financialError) return alert(financialError);
    if (!loadedDoc?._id) {
      return alert('Load an invoice first');
    }

    if (!customerName.trim()) {
      return alert('Customer name is required');
    }

    if (feeLines.length === 0 || gross <= 0) {
      return alert('Add at least one item with amount > 0');
    }

    if (totalAmount <= 0) {
      return alert('Total must be > 0 (check discount).');
    }

    if (whatsapp && !isValidWhatsApp(whatsapp)) {
      return alert('Please enter a valid WhatsApp number');
    }

    const alreadyReceived = Number(
      loadedDoc.amountReceived || 0
    );

    const newPaymentAmount = Number(extraPayment || 0);
    if (newPaymentAmount > 0 && !validPaymentDay(extraPaymentDate)) return alert('A valid Payment Date is required');

    if (toPaise(totalAmount) < toPaise(alreadyReceived)) {
      return alert(
        `Invoice total (INR ${inr(
          totalAmount
        )}) cannot be less than amount already received (INR ${inr(
          alreadyReceived
        )}).`
      );
    }

    if (newPaymentAmount < 0) {
      return alert('New payment cannot be negative.');
    }

    if (
      newPaymentAmount > 0 &&
      sumPaise([toPaise(alreadyReceived), toPaise(newPaymentAmount)]) > toPaise(totalAmount)
    ) {
      return alert('Payment exceeds updated invoice total.');
    }

    try {
      setIsSubmitting(true);

      const finalReceived = fromPaise(sumPaise([toPaise(alreadyReceived), toPaise(newPaymentAmount)]));

      const finalDue = fromPaise(Math.max(toPaise(totalAmount) - toPaise(finalReceived), 0));

      const finalStatus =
        finalDue <= 0
          ? 'paid'
          : finalReceived > 0
            ? 'partial'
            : 'due';

      const updatePayload = {
        expectedVersion: loadedDoc.__v ?? 0,
        customerName,
        whatsapp,
        organization,
        paymentMode,

        // Preserve permanent IDs and product references.
        items: feeLines.map((item) => ({
          ...(item._id ? { _id: item._id } : {}),
          ...(item.productId
            ? { productId: item.productId }
            : {}),
          label: item.label,
          amount: Number(item.amount || 0),
          qty: Math.max(1, Number(item.qty) || 1),
        })),

        discount: Number(discount || 0),
        totalAmount,
        amountReceived: finalReceived,
        balanceDue: finalDue,
        status: finalStatus,
        notes,
        invoiceNumber,

        newPayment:
          newPaymentAmount > 0
            ? {
                amount: newPaymentAmount,
                mode: extraPaymentMode,
                date: extraPaymentDate || undefined,
              }
            : null,
      };

      const response = await axios.put(
        `/api/running/${loadedDoc._id}`,
        updatePayload
      );

      const updatedDoc = response.data;

      populateInvoice(updatedDoc);
      setSavedInvoiceId(updatedDoc._id);

      alert('Invoice updated successfully.');

      // No automatic PDF download when editing.
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error.message;

      console.error('Invoice update failed:', error);

      alert(`Update failed: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="max-w-2xl mx-auto px-4" onChangeCapture={() => setSavedInvoiceId(null)}>
      <div className="bg-white p-6 md:p-8 rounded-xl shadow ring-1 ring-gray-100">
        <h2 className="text-2xl font-semibold mb-2">
          Running Customer Payment
        </h2>

        <p className="text-sm text-gray-500 mb-4">
          Record ad-hoc sales for walk-in customers. Items support
          multiple rows and an optional discount.
        </p>

        <hr className="my-4 border-gray-200" />

        {/* INVOICE SELECTION */}

        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">
              Invoice No.
            </label>

            <input
              className="border px-2 py-1 rounded w-40"
              placeholder="e.g., INV000123"
              value={invoiceNumber}
              disabled={existingMode && !!loadedDoc}
              onChange={(event) =>
                setInvoiceNumber(event.target.value.toUpperCase())
              }
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">
              Invoice Date
            </label>

            <input
              type="date"
              className="border px-2 py-1 rounded"
              aria-label="Invoice Date"
              value={invoiceDate}
              disabled={existingMode}
              onChange={(event) =>
                setInvoiceDate(event.target.value)
              }
            />
          </div>

          <button
            type="button"
            onClick={() => {
              if (existingMode) {
                resetForm();
              } else {
                setExistingMode(true);
              }
            }}
            className="ml-auto bg-gray-100 hover:bg-gray-200 text-gray-900 px-3 py-1.5 rounded"
          >
            {existingMode
              ? '← New Invoice'
              : 'Existing Invoice Payment?'}
          </button>

          {existingMode && !loadedDoc && (
            <button
              type="button"
              onClick={() => loadInvoice(invoiceNumber)}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded"
            >
              Load
            </button>
          )}
        </div>

        {/* CUSTOMER DETAILS */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">
              Customer Name
            </label>

            <input
              className="w-full border px-2 py-1 rounded"
              value={customerName}
              onChange={(event) =>
                setCustomerName(event.target.value)
              }
            />
          </div>

          <div>
            <label className="block text-sm mb-1">
              WhatsApp Number
            </label>

            <input
              className="w-full border px-2 py-1 rounded"
              value={whatsapp}
              onChange={(event) => {
                const value = sanitizeWhatsApp(
                  event.target.value
                );

                setWhatsapp(value);

                if (value && !isValidWhatsApp(value)) {
                  setWhatsappError(
                    'Only digits and an optional leading +; 7–15 digits.'
                  );
                } else {
                  setWhatsappError('');
                }
              }}
              onBlur={() => {
                if (whatsapp && !isValidWhatsApp(whatsapp)) {
                  setWhatsappError(
                    'Invalid WhatsApp number format'
                  );
                }
              }}
            />

            {whatsappError && (
              <p className="text-xs text-red-600 mt-1">
                {whatsappError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1">
              Organization
            </label>

            <select aria-label="Invoice organization" disabled={existingMode && !!loadedDoc}
              className="w-full border px-2 py-1 rounded"
              value={organization}
              onChange={(event) => {

  const nextOrganization = event.target.value;

  setOrganization(nextOrganization);

  if (!existingMode) {

    setFeeLines([blankItem()]);


  }

}}
            >
              <option value="The Bengal Craft">
                The Bengal Craft
              </option>
              <option value="COG">COG</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">
              Payment Mode
            </label>

            <select
              className="w-full border px-2 py-1 rounded"
              value={paymentMode}
              onChange={(event) =>
                setPaymentMode(event.target.value)
              }
            >
              <option value="UPI">UPI</option>
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="NetBanking">NetBanking</option>
              <option value="Others">Others</option>
            </select>
          </div>
        </div>

        {/* ORDER ITEMS */}

        <div className="mt-4">
          <label className="block text-sm mb-1 text-gray-700">
            Payment Items
          </label>

          <button type="button" className="text-sm text-indigo-700 mb-2" disabled={productsLoading} onClick={() => setProductRevision(value => value + 1)}>Refresh products</button>

{/* ITEM ROWS */}

{feeLines.map((line, index) => (
  <div
    key={line._id || index}
    className="flex flex-col sm:flex-row gap-2 mb-3"
  >

    {/* EXISTING INVOICE: ORIGINAL DESCRIPTION */}

    {line._id ? (
      <div className="flex-1 min-w-0">
        <input
          className="w-full border px-2 py-1 rounded"
          placeholder="Original item description"
          value={line.label}
          onChange={(event) =>
            handleLineChange(
              index,
              'label',
              event.target.value
            )
          }
        />

        {line.productId ? (
          <p className="text-xs text-gray-500 mt-1">
            Historical Product Master mapping retained; stored description and price are unchanged by catalog updates.
          </p>
        ) : <p className="text-xs text-amber-700 mt-1">Legacy / custom unmapped item - original values retained. Use Product Mapping to standardize it.</p>}
      </div>
    ) : (

      <InvoiceProductSelector products={products} organization={organization} value={line.productId}
        rowNumber={index + 1} loading={productsLoading} error={productsError} onSelect={product => handleProductChange(index, product)} />
    )}

    {/* QUANTITY */}

    <input
      className="w-20 border px-2 py-1 rounded text-right"
      type="number"
      min="1"
      step="1"
      placeholder="Qty"
      value={line.qty}
      onChange={(event) =>
        handleLineChange(
          index,
          'qty',
          Math.max(
            1,
            Math.floor(Number(event.target.value) || 1)
          )
        )
      }
    />

    {/* UNIT PRICE */}

    <input
      className="w-32 border px-2 py-1 rounded text-right"
      type="number"
      min="0"
      step="0.01"
      placeholder="Unit Price"
      value={line.amount}
      onChange={(event) =>
        handleLineChange(
          index,
          'amount',
          event.target.value
        )
      }
    />

    {/* REMOVE ITEM */}

    <button
      type="button"
      title="Remove item"
      aria-label="Remove item"
      onClick={() => handleRemoveLine(index)}
      className="text-red-600 font-bold"
    >
      ✕
    </button>

  </div>
))}

          <button
            type="button"
            onClick={handleAddLine}
            className="text-blue-600 text-sm mt-1"
          >
            + Add Item
          </button>
        </div>

        {/* DISCOUNT, RECEIPTS AND NOTES */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div>
            <label className="block text-sm mb-1 text-gray-700">
              Discount (INR)
            </label>

            <input
              className="w-full border px-2 py-1 rounded"
              type="number"
              min="0"
              value={discount}
              onChange={(event) =>
                setDiscount(Number(event.target.value) || 0)
              }
            />
          </div>

          {!existingMode && (
            <div>
              <label className="block text-sm mb-1 text-gray-700">
                Amount Received (INR)
              </label>

              <input
                className="w-full border px-2 py-1 rounded"
                type="number"
                min="0"
                aria-label="Amount Received (INR)"
                value={amountReceived}
                onChange={(event) => {
                  const raw = Number(
                    event.target.value || 0
                  );

                  const clamped = Math.min(
                    Math.max(raw, 0),
                    Math.max(totalAmount, 0)
                  );

                  setAmountReceived(clamped);
                  setReceivedError('');
                }}
              />

              {receivedError && (
                <p className="text-xs text-red-600 mt-1">
                  {receivedError}
                </p>
              )}
            </div>
          )}

          {existingMode && loadedDoc && (
            <div className="bg-gray-50 border rounded p-3 text-sm">
              <div className="text-gray-600">
                Amount Already Received
              </div>

              <div className="font-semibold">
                INR {inr(loadedDoc.amountReceived)}
              </div>

              <div className="text-gray-600 mt-2">
                Current Due
              </div>

              <div className="font-semibold">
                INR {inr(loadedDoc.balanceDue)}
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm mb-1 text-gray-700">
              Notes (optional)
            </label>

            <textarea
              className="w-full border px-2 py-1 rounded"
              value={notes}
              onChange={(event) =>
                setNotes(event.target.value)
              }
            />
          </div>
        </div>

        {financialError && <p role="alert" className="mt-4 rounded bg-amber-50 p-3 text-amber-900">{financialError}. Historical values are unchanged; reconcile unsupported monetary precision before saving.</p>}
        {/* FINANCIAL SUMMARY */}

        <div className="mt-4 bg-gray-50 rounded-lg p-3 md:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-sm text-gray-600">
            Status:{' '}
            <span
              className={
                financialError || dispDue > 0
                  ? 'text-amber-600 font-medium'
                  : 'text-emerald-600 font-medium'
              }
            >
              {dispStatus.toUpperCase()}
            </span>
          </div>

          <div className="text-right font-semibold space-y-0.5">
            <div>Gross (INR): {inr(gross)}</div>
            <div>Discount (INR): {inr(discount)}</div>
            <div>Total (INR): {inr(totalAmount)}</div>
            <div>Received (INR): {inr(dispReceived)}</div>
            <div>Due (INR): {inr(dispDue)}</div>
          </div>
        </div>

        {/* EXISTING INVOICE: PAYMENT AND EDITING */}

        {existingMode && loadedDoc && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-sm font-medium mb-2">
              Existing Invoice: Save Changes / Add Payment
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="block text-sm mb-1">
                  New Payment (INR)
                </label>

                <input
                  className="w-full border px-2 py-1 rounded"
                  type="number"
                  min="0"
                  aria-label="New Payment (INR)"
                  value={extraPayment}
                  onChange={(event) =>
                    setExtraPayment(
                      Math.max(
                        Number(event.target.value || 0),
                        0
                      )
                    )
                  }
                />
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Mode
                </label>

                <select
                  className="w-full border px-2 py-1 rounded"
                  value={extraPaymentMode}
                  onChange={(event) =>
                    setExtraPaymentMode(event.target.value)
                  }
                >
                  <option value="UPI">UPI</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="NetBanking">NetBanking</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Payment Date
                </label>

                <input
                  className="w-full border px-2 py-1 rounded"
                  type="date"
                  aria-label="Payment Date"
                  required={Number(extraPayment) > 0}
                  value={extraPaymentDate}
                  onChange={(event) =>
                    setExtraPaymentDate(event.target.value)
                  }
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isSubmitting || !!financialError}
                onClick={handleUpdate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>

              <button
                type="button"
                disabled={receiptBusy || isSubmitting}
                onClick={() => downloadReceipt(loadedDoc)}
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-3 py-1.5 rounded"
              >
                Download Receipt
              </button>
            </div>

            {savedInvoiceId && <div role="status" className="mt-3 text-sm text-emerald-800">
              Invoice saved. <button type="button" className="border rounded px-3 py-1.5 bg-white ml-2" onClick={() => setDeliveryId(savedInvoiceId)}>Open Delivery</button>
            </div>}

            {/* PAYMENT HISTORY */}

            {Array.isArray(loadedDoc.payments) &&
              loadedDoc.payments.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-medium mb-1">
                    Payment History
                  </div>

                  <ul className="text-sm text-gray-700 list-disc pl-5">
                    {loadedDoc.payments.map((payment, index) => (
                      <li key={payment._id || index}>
                        {new Date(
                          payment.date
                        ).toLocaleDateString('en-IN')}
                        {' — '}
                        {payment.mode || '-'}
                        {' — INR '}
                        {inr(payment.amount)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        )}

        {/* CREATE INVOICE BUTTON */}

        {!existingMode && (
          <button
            type="button"
            disabled={isSubmitting || !!financialError}
            onClick={handleSubmit}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {isSubmitting
              ? 'Saving...'
              : received > 0
                ? 'Create Invoice & Record Payment'
                : 'Create Invoice'}
          </button>
        )}
      </div>
      {deliveryId && <DeliveryPanel key={deliveryId} invoiceId={deliveryId} onClose={closeDelivery} onChanged={onDeliveryChanged} />}
    </div>
  );
}